import React, { useContext, useEffect, useState } from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { Adjust } from "@mui/icons-material";

import { MapContext } from "../MapProvider";

import { IButtonProps } from "./Toolbar";
import ToolbarToggleButton from "./ToolbarToggleButton";

const { constants: Constants } = MapboxDraw;

interface GenericActionButtonProps extends IButtonProps {
  /**
   * A string that defines the type of action to be performed. This is used to identify the action in event listeners. Defaults to "generic".
   */
  actionType?: string;
}

/**
 * A generic action button that triggers a custom action on the selected features in the map. This button is designed to be flexible and can be used for various actions, such as exporting selected features, performing calculations, or any other custom functionality defined by the application.
 */
const GenericActionButton: React.FC<GenericActionButtonProps> = ({ actionType = "generic", icon, title = "Perform action", ...rest }) => {
  const { map, drawControl } = useContext(MapContext);
  const [hasSelection, setHasSelection] = useState(false);

  useEffect(() => {
    const updateSelection = () => {
      setHasSelection((drawControl.current?.getSelected()?.features?.length ?? 0) > 0);
    };

    map?.on(Constants.events.SELECTION_CHANGE, updateSelection);
    return () => {
      map?.off(Constants.events.SELECTION_CHANGE, updateSelection);
    };
  }, [map, drawControl]);

  const handleOnClick = () => {
    if (!drawControl.current) return;
    map?.fire("draw.action", { actionType, features: drawControl.current.getSelected().features });
  };

  return (
    <ToolbarToggleButton
      title={title}
      {...rest}
      value="generic_action_button"
      onClick={handleOnClick}
      data-testid="toolbar-generic-action-button"
      disabled={!hasSelection}
    >
      {icon || <Adjust />}
    </ToolbarToggleButton>
  );
};

export default GenericActionButton;
